import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Lock, User } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Input } from '@/shared/ui/inputs/Input';
import { Button } from '@/shared/ui/buttons/Button';
import { useStaffLogin } from '@/features/auth/modules/models/useStaffLogin';
import { loginSchema, type LoginValues } from '@/features/auth/modules/types';

export const FeaturesAuthLoginForm = () => {
  const { t } = useTranslation();
  const login = useStaffLogin();
  const form = useForm<LoginValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: { username: '', password: '' },
    mode: 'onBlur',
  });

  const usernameError = form.formState.errors.username?.message;
  const passwordError = form.formState.errors.password?.message;

  return (
    <form
      onSubmit={form.handleSubmit((v) => void login.submit(v))}
      className="flex flex-col gap-4"
      data-cy="login-form"
    >
      <Input
        label={t('login.username')}
        autoComplete="username"
        autoFocus
        prefix={<User className="h-4 w-4" />}
        error={usernameError ? t(usernameError) : undefined}
        {...form.register('username')}
      />
      <Input
        label={t('login.password')}
        type="password"
        autoComplete="current-password"
        prefix={<Lock className="h-4 w-4" />}
        error={passwordError ? t(passwordError) : undefined}
        {...form.register('password')}
      />
      {login.error && (
        <p role="alert" className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
          {login.error}
        </p>
      )}
      <Button type="submit" size="lg" loading={login.isLoading} block>
        {t('login.submit')}
      </Button>
    </form>
  );
};
